import type { LocalizableResource } from '~/composables/types/localizable'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import { getStrapiDynamicPageLocalizationsQuery } from '~/composables/strapiGraphqlHelpers'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'

const FETCH_TIMEOUT_MS = 10_000

export const useDynamicStrapiPage = (slug: string) => {
  const { locale } = useI18n()
  const config = useRuntimeConfig()
  const preview = useRoute().query.preview === 'true'
  const { strapiGraphqlURL, strapiDraftGraphqlURL, strapiApiKey } =
    config.public
  const baseURL = preview ? strapiDraftGraphqlURL : strapiGraphqlURL

  const fullSlug = `${slug}_${locale.value}`
  const bcp47Locale = convertLocaleIntoBCP47(locale.value)
  const cacheKey = `dynamic-strapi-page-localizations-${fullSlug}-${bcp47Locale}`

  return useAsyncData<LocalizableResource | null, Error | undefined>(
    cacheKey,
    async (): Promise<LocalizableResource | null> => {
      try {
        const response = await $fetch<
          StrapiApiDefaultResponse<{ pages: LocalizableResource[] }>
        >(baseURL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${strapiApiKey}`,
            'Content-Type': 'application/json',
          },
          body: {
            operationName: null,
            variables: { status: preview ? 'DRAFT' : 'PUBLISHED' },
            query: getStrapiDynamicPageLocalizationsQuery(
              fullSlug,
              bcp47Locale
            ),
          },
          timeout: FETCH_TIMEOUT_MS,
        })

        return response?.data?.pages?.[0] ?? null
      } catch {
        return null
      }
    },
    {
      server: true,
      lazy: false,
      dedupe: 'defer',
      getCachedData: (key) => useNuxtData(key).data.value ?? undefined,
    }
  )
}
